import { useEffect, useRef } from "react";
import { applyGooglePublisherTagPageConfig } from "@/components/common/GooglePublisherTag/pageLevelConfig";

interface InterstitialLifecycleConfig {
  requireStorageAccess?: boolean;
  triggers?: {
    navBar?: boolean;
    unhideWindow?: boolean;
    inactivity?: boolean;
    endOfArticle?: boolean;
    backward?: boolean;
  };
}

interface Interstitial1Props {
  slot: string;
  closeFallbackMs?: number;
  interstitialConfig?: InterstitialLifecycleConfig;
  onRequestFilled?: () => void;
  onRequestEmpty?: () => void;
  onUnsupported?: () => void;
  onImpressionViewable?: () => void;
  onClosed?: () => void;
}

/**
 * m-qs 菜单切换用的一次性 GPT interstitial。
 * 每次外部挂载一次，就只发起一条新的插屏请求；请求完成后由外部卸载。
 */
export default function Interstitial1(props: Interstitial1Props) {
  const {
    slot,
    interstitialConfig,
    onRequestFilled,
    onRequestEmpty,
    onUnsupported,
    onImpressionViewable,
    onClosed,
  } = props;
  const slotRef = useRef<any>(null);
  const impressionViewableRef = useRef(false);
  const isClosedRef = useRef(false);
  const onRequestFilledRef = useRef(onRequestFilled);
  const onRequestEmptyRef = useRef(onRequestEmpty);
  const onUnsupportedRef = useRef(onUnsupported);
  const onImpressionViewableRef = useRef(onImpressionViewable);
  const onClosedRef = useRef(onClosed);
  const teardownRef = useRef<() => void>(() => {});

  useEffect(() => {
    onRequestFilledRef.current = onRequestFilled;
    onRequestEmptyRef.current = onRequestEmpty;
    onUnsupportedRef.current = onUnsupported;
    onImpressionViewableRef.current = onImpressionViewable;
    onClosedRef.current = onClosed;
  }, [onClosed, onImpressionViewable, onRequestEmpty, onRequestFilled, onUnsupported]);

  useEffect(() => {
    if (!slot) {
      return;
    }

    impressionViewableRef.current = false;
    isClosedRef.current = false;
    window.googletag = window.googletag || { cmd: [] };

    const teardownSlot = () => {
      const pubads = window.googletag?.pubads?.();
      pubads?.removeEventListener?.("slotRenderEnded", handleSlotRenderEnded);
      pubads?.removeEventListener?.("impressionViewable", handleImpressionViewable);
      pubads?.removeEventListener?.("slotVisibilityChanged", handleSlotVisibilityChanged);

      if (slotRef.current) {
        // interstitial 关闭后要立即离开 GPT 的 long-lived pageview，
        // 否则紧接着发起的普通广告请求仍可能被它的竞争排除规则影响。
        window.googletag?.destroySlots?.([slotRef.current]);
        slotRef.current = null;
      }
    };

    const emitClosed = () => {
      if (isClosedRef.current) {
        return;
      }

      isClosedRef.current = true;
      teardownSlot();
      onClosedRef.current?.();
    };

    const handleSlotRenderEnded = (event: any) => {
      if (event?.slot !== slotRef.current) {
        return;
      }

      console.info("[Interstitial1] slotRenderEnded", {
        slot,
        isEmpty: event.isEmpty,
        lineItemId: event.lineItemId,
        creativeId: event.creativeId,
        size: event.size,
      });

      if (event.isEmpty) {
        onRequestEmptyRef.current?.();
        return;
      }

      onRequestFilledRef.current?.();
    };

    const handleImpressionViewable = (event: any) => {
      if (event?.slot !== slotRef.current) {
        return;
      }

      impressionViewableRef.current = true;
      console.info("[Interstitial1] impressionViewable", { slot });
      onImpressionViewableRef.current?.();
    };

    const handleSlotVisibilityChanged = (event: any) => {
      if (event?.slot !== slotRef.current || !impressionViewableRef.current) {
        return;
      }

      if (event.inViewPercentage !== 0) {
        return;
      }

      // 仅把“页面仍在前台时的可见度归零”视为真实关闭，
      // 避免切后台/切标签时把 GPT 的隐藏态误判成用户关闭。
      if (document.visibilityState !== "visible") {
        return;
      }

      emitClosed();
    };

    window.googletag.cmd.push(() => {
      const pubads = window.googletag.pubads();
      pubads.addEventListener("slotRenderEnded", handleSlotRenderEnded);
      pubads.addEventListener("impressionViewable", handleImpressionViewable);
      pubads.addEventListener("slotVisibilityChanged", handleSlotVisibilityChanged);

      const interstitialSlot = window.googletag.defineOutOfPageSlot(
        slot,
        window.googletag.enums.OutOfPageFormat.INTERSTITIAL,
      );

      if (!interstitialSlot) {
        console.warn("[Interstitial1] 当前设备或页面环境不支持 GPT web interstitial");
        onUnsupportedRef.current?.();
        return;
      }

      slotRef.current = interstitialSlot;
      interstitialSlot.setConfig({
        interstitial: {
          requireStorageAccess: interstitialConfig?.requireStorageAccess ?? false,
          triggers: {
            navBar: interstitialConfig?.triggers?.navBar ?? false,
            unhideWindow: interstitialConfig?.triggers?.unhideWindow ?? false,
            inactivity: interstitialConfig?.triggers?.inactivity ?? false,
            endOfArticle: interstitialConfig?.triggers?.endOfArticle ?? false,
            backward: interstitialConfig?.triggers?.backward ?? false,
          },
        },
      });

      interstitialSlot.addService(pubads);
      applyGooglePublisherTagPageConfig();
      window.googletag.enableServices();
      window.googletag.display(interstitialSlot);
    });

    teardownRef.current = () => {
      window.googletag?.cmd?.push?.(() => {
        teardownSlot();
      });
    };

    return () => {
      teardownRef.current();
    };
  }, [
    interstitialConfig?.requireStorageAccess,
    interstitialConfig?.triggers?.navBar,
    interstitialConfig?.triggers?.unhideWindow,
    interstitialConfig?.triggers?.inactivity,
    interstitialConfig?.triggers?.endOfArticle,
    interstitialConfig?.triggers?.backward,
    slot,
  ]);

  return null;
}
